import LinkCard, { type LinkCardProps } from '@/components/patterns/link-card'
import type { BadgeVariant } from '@/components/ui/badge'
import type { ComponentProps, ReactElement } from 'react'

interface FulfillmentPresentation {
  badgeVariant: BadgeVariant
  tone: string
  surfaceClassName: string
}

interface FulfillmentProps {
  fulfillmentType?: string | null
}
import { FulfillmentType } from '@/lib/enums/FulfillmentType'
import { cn } from '@/lib/utils'

const fulfillmentPresentation: Partial<Record<string, FulfillmentPresentation>> = {
  [FulfillmentType.Delivery.Value]: {
    badgeVariant: 'periwinkle',
    tone: 'delivery',
    surfaceClassName:
      'border-status-info-border/55 bg-status-info-background text-status-info-foreground',
  },
  [FulfillmentType.Pickup.Value]: {
    badgeVariant: 'brand',
    tone: 'pickup',
    surfaceClassName:
      'border-selection-border/35 bg-accentSecondary text-accentSecondary-foreground',
  },
  [FulfillmentType.InStore.Value]: {
    badgeVariant: 'muted',
    tone: 'in-store',
    surfaceClassName:
      'border-chart-3/25 bg-brand-mint/70 text-brand-charcoal dark:border-chart-3/30 dark:bg-chart-3/10 dark:text-foreground',
  },
  [FulfillmentType.Shipping.Value]: {
    badgeVariant: 'info',
    tone: 'shipping',
    surfaceClassName:
      'border-status-info-border/55 bg-status-info-background text-status-info-foreground',
  },
}

const defaultFulfillmentPresentation: FulfillmentPresentation = {
  badgeVariant: 'secondary',
  tone: 'neutral',
  surfaceClassName: 'border-border/60 bg-secondary text-secondary-foreground',
}

function getFulfillmentPresentation(fulfillmentType?: string | null): FulfillmentPresentation {
  return fulfillmentPresentation[fulfillmentType ?? ''] ?? defaultFulfillmentPresentation
}

function FulfillmentSurface({
  className,
  fulfillmentType,
  ...props
}: ComponentProps<'div'> & FulfillmentProps): ReactElement {
  const presentation = getFulfillmentPresentation(fulfillmentType)

  return (
    <div
      data-fulfillment-type={fulfillmentType ?? 'unknown'}
      data-fulfillment-tone={presentation.tone}
      className={cn(presentation.surfaceClassName, className)}
      {...props}
    />
  )
}

function FulfillmentLinkCard({
  className,
  fulfillmentType,
  ...props
}: LinkCardProps & FulfillmentProps): ReactElement {
  const presentation = getFulfillmentPresentation(fulfillmentType)

  return (
    <LinkCard
      data-fulfillment-type={fulfillmentType ?? 'unknown'}
      data-fulfillment-tone={presentation.tone}
      className={cn(presentation.surfaceClassName, className)}
      {...props}
    />
  )
}

export { FulfillmentLinkCard, FulfillmentSurface, getFulfillmentPresentation }
